import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { 
  DollarSign, TrendingUp, Cpu, RefreshCw, 
  AlertCircle, ArrowUpRight, ArrowDownRight, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function CostDashboard({ adminToken }) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [overview, setOverview] = useState(null);
  const [dailyCosts, setDailyCosts] = useState([]);
  const [userCosts, setUserCosts] = useState([]);
  const [modelCosts, setModelCosts] = useState([]);
  const [profitMargin, setProfitMargin] = useState(null);

  const fetchData = async () => {
    const token = adminToken || localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Not authenticated — please log in again');
      return;
    }
    const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

    setLoading(true);
    try {
      const [overviewRes, dailyRes, userRes, modelRes, profitRes] = await Promise.all([
        axios.get(`${API}/api/admin/costs/overview?period=${period}`, axiosConfig),
        axios.get(`${API}/api/admin/costs/daily?days=30`, axiosConfig),
        axios.get(`${API}/api/admin/costs/by-user?period=${period}&limit=20`, axiosConfig),
        axios.get(`${API}/api/admin/costs/by-model?period=${period}`, axiosConfig),
        axios.get(`${API}/api/admin/costs/profit-margin?period=${period}`, axiosConfig)
      ]);

      setOverview(overviewRes.data);
      setDailyCosts(dailyRes.data);
      setUserCosts(userRes.data);
      setModelCosts(modelRes.data);
      setProfitMargin(profitRes.data);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        localStorage.removeItem('admin_token');
        toast.error('Admin session expired — please log in again');
        window.location.reload();
      } else {
        toast.error(`Failed to fetch cost data: ${error?.response?.data?.detail || error.message}`);
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const formatCurrency = (amount, currency = 'USD') => {
    if (currency === 'INR') {
      return `₹${amount?.toLocaleString() || '0'}`;
    }
    return `$${amount?.toFixed(2) || '0.00'}`;
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-900">Service Cost Dashboard</h1>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Last 7 days</SelectItem>
              <SelectItem value="month">Last 30 days</SelectItem>
              <SelectItem value="quarter">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Profit Margin Summary */}
      {profitMargin && (
        <Card className={`p-6 ${profitMargin.margin_percent >= 50 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-zinc-800">Profit Margin</h3>
              <p className="text-sm text-zinc-600">Revenue vs Processing Costs ({period})</p>
            </div>
            <div className="text-right">
              <div className={`text-3xl font-bold ${profitMargin.margin_percent >= 50 ? 'text-emerald-700' : 'text-amber-700'}`}>
                {profitMargin.margin_percent}%
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="text-emerald-700">Revenue: {formatCurrency(profitMargin.revenue_inr, 'INR')}</span>
                <span className="text-red-600">Service Cost: {formatCurrency(profitMargin.ai_cost_inr, 'INR')}</span>
                <span className="text-zinc-800 font-semibold">Profit: {formatCurrency(profitMargin.gross_profit_inr, 'INR')}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Service Cost</p>
                <p className="text-2xl font-bold text-zinc-900">{formatCurrency(overview.totals?.cost_usd)}</p>
                <p className="text-xs text-zinc-500">{formatCurrency(overview.totals?.cost_inr, 'INR')}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Cpu className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Requests</p>
                <p className="text-2xl font-bold text-zinc-900">{overview.totals?.total_requests?.toLocaleString()}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Input Tokens</p>
                <p className="text-2xl font-bold text-zinc-900">
                  {(overview.totals?.input_tokens / 1000000)?.toFixed(2)}M
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Zap className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Output Tokens</p>
                <p className="text-2xl font-bold text-zinc-900">
                  {(overview.totals?.output_tokens / 1000000)?.toFixed(2)}M
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Cost Trend */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Daily Processing Costs</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyCosts}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(val) => `$${val}`} />
              <Tooltip formatter={(val) => formatCurrency(val)} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="cost_usd" 
                stroke="#ef4444" 
                name="Cost (USD)"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Cost by Provider */}
        {overview?.by_provider && Object.keys(overview.by_provider).length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Cost by Provider</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(overview.by_provider).map(([provider, data]) => ({
                    name: provider,
                    value: data.cost
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  dataKey="value"
                >
                  {Object.entries(overview.by_provider).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(val) => formatCurrency(val)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Cost by Model */}
      {modelCosts.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Cost by Model</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Provider</th>
                  <th className="text-left p-2">Model</th>
                  <th className="text-right p-2">Requests</th>
                  <th className="text-right p-2">Input Tokens</th>
                  <th className="text-right p-2">Output Tokens</th>
                  <th className="text-right p-2">Cost (USD)</th>
                  <th className="text-right p-2">Avg/Request</th>
                </tr>
              </thead>
              <tbody>
                {modelCosts.map((model, idx) => (
                  <tr key={idx} className="border-b hover:bg-zinc-50">
                    <td className="p-2 font-medium capitalize">{model.provider}</td>
                    <td className="p-2">{model.model}</td>
                    <td className="p-2 text-right">{model.requests?.toLocaleString()}</td>
                    <td className="p-2 text-right">{(model.input_tokens / 1000)?.toFixed(1)}K</td>
                    <td className="p-2 text-right">{(model.output_tokens / 1000)?.toFixed(1)}K</td>
                    <td className="p-2 text-right font-medium text-red-600">{formatCurrency(model.cost_usd)}</td>
                    <td className="p-2 text-right text-zinc-500">${model.avg_cost_per_request?.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Top Users by Cost */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Top Users by Service Cost</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Plan</th>
                <th className="text-right p-2">Requests</th>
                <th className="text-right p-2">Tokens</th>
                <th className="text-right p-2">Cost (USD)</th>
                <th className="text-right p-2">Cost (INR)</th>
              </tr>
            </thead>
            <tbody>
              {userCosts.map((user, idx) => (
                <tr key={idx} className="border-b hover:bg-zinc-50">
                  <td className="p-2">
                    <div>
                      <p className="font-medium">{user.name || 'N/A'}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      user.plan === 'professional' ? 'bg-indigo-100 text-indigo-700' :
                      user.plan === 'agency' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-zinc-100 text-zinc-700'
                    }`}>
                      {user.plan || 'Free'}
                    </span>
                  </td>
                  <td className="p-2 text-right">{user.requests?.toLocaleString()}</td>
                  <td className="p-2 text-right">{(user.tokens / 1000)?.toFixed(1)}K</td>
                  <td className="p-2 text-right font-medium text-red-600">{formatCurrency(user.cost_usd)}</td>
                  <td className="p-2 text-right text-zinc-500">{formatCurrency(user.cost_inr, 'INR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Cost by Service */}
      {overview?.by_service && Object.keys(overview.by_service).length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Cost by Service</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={Object.entries(overview.by_service).map(([service, data]) => ({
              service,
              cost: data.cost,
              requests: data.requests
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="service" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(val) => `$${val}`} />
              <Tooltip formatter={(val, name) => name === 'cost' ? formatCurrency(val) : val} />
              <Legend />
              <Bar dataKey="cost" fill="#ef4444" name="Cost (USD)" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
